export type MapLayerArcgis = {
  type: string;
  proxyBase: string;
  mapServerPath: string;
  visibleLayers: number[];
  opacity: number;
  label: string;
  defaultVisible?: boolean;
};

export type MapOverlayLayer = MapLayerArcgis & {
  id: string;
  defaultVisible: boolean;
};

export type MapConfig = {
  bounds: { south: number; west: number; north: number; east: number };
  envelope?: { south: number; west: number; north: number; east: number };
  clusterBoundaryUrl?: string;
  center: [number, number];
  minZoom: number;
  maxZoom: number;
  defaultZoom: number;
  layers: {
    basemap: { type: string; url: string; attribution?: string; subdomains?: string };
    compilation?: MapLayerArcgis;
    overlays?: MapOverlayLayer[];
  };
  gisnet?: {
    configured?: boolean;
    migrashLayerNote?: string;
  };
  attribution: string;
  geocode?: {
    provider: 'google' | 'nominatim';
  };
};

export type PlanParcelBinding = {
  plan_number: string;
  plan_type: string;
  status: string;
  designation_plan: string;
  published_at?: string;
  plan_url?: string;
  plan_name?: string;
};

export type ParcelSelectionMode = 'point' | 'polygon_drawn';

export type GeoJsonPolygon = {
  type: 'Polygon';
  coordinates: number[][][];
};

export type ParcelIdentifyResult = {
  /** Committee planning-lot — absent when only cadastral גוש/חלקה is known. */
  migrash?: string;
  gush?: string;
  helka?: string;
  lat: number;
  lng: number;
  settlement_name?: string;
  /** ייעוד קרקע (compilation layer 8) — used for quick assessment. */
  designation?: string;
  /** XPlan TYPE_CODE for legend-backed ramzor */
  designation_code?: string;
  designation_compilation?: string;
  plan_primary?: string;
  plans_affecting?: PlanParcelBinding[];
  area_sqm_registered?: number;
  geometry?: { type: string; coordinates: unknown };
  selection_mode?: ParcelSelectionMode;
  area_sqm_drawn?: number;
  source: string;
};

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export type DesignationTrafficLight = 'green' | 'yellow' | 'red' | 'unknown';

export type MapLegendEntry = {
  label: string;
  color: string;
  code: string;
  pattern: 'solid' | 'hatch';
  traffic_light?: DesignationTrafficLight;
};

export type MapLegendResponse = {
  layerName: string;
  districtId: string;
  summaryEntries: MapLegendEntry[];
  entries: MapLegendEntry[];
  totalClasses: number;
  source: 'xplan';
};

export type MapGeocodeHit = {
  name: string;
  displayName: string;
  lat: number;
  lng: number;
  source: 'google' | 'nominatim' | 'registry';
};

export type MapGeocodeResponse = {
  hits: MapGeocodeHit[];
  provider: 'google' | 'nominatim';
};

const geocodeCache = new Map<string, MapGeocodeResponse>();

export async function fetchMapGeocode(
  query: string,
  limit = 8,
): Promise<MapGeocodeResponse> {
  const trimmed = query.trim();
  const cacheKey = `${normalizeGeocodeKey(trimmed)}:${limit}`;
  const cached = geocodeCache.get(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams({ q: trimmed, limit: String(limit) });
  const res = await fetch(`/api/map/geocode?${params}`, {
    signal: AbortSignal.timeout(6_000),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Geocode failed (${res.status})`);
  }
  const data = (await res.json()) as MapGeocodeResponse;
  geocodeCache.set(cacheKey, data);
  return data;
}

function normalizeGeocodeKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

export async function fetchMapConfig(): Promise<MapConfig> {
  return parseJson(await fetch('/api/map/config'));
}

export async function fetchMapLegend(options?: {
  lat?: number;
  lng?: number;
}): Promise<MapLegendResponse> {
  const params = new URLSearchParams();
  if (options?.lat != null && options?.lng != null) {
    params.set('lat', String(options.lat));
    params.set('lng', String(options.lng));
  }
  const qs = params.toString();
  return parseJson(
    await fetch(`/api/map/legend${qs ? `?${qs}` : ''}`),
  );
}

export async function identifyMapParcel(
  lat: number,
  lng: number,
  geometry?: GeoJsonPolygon,
): Promise<ParcelIdentifyResult> {
  try {
    return await parseJson(
      await fetch('/api/map/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          geometry ? { lat, lng, geometry } : { lat, lng },
        ),
        signal: AbortSignal.timeout(15_000),
      }),
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new Error('IDENTIFY_TIMEOUT');
    }
    throw err;
  }
}

export async function lookupParcelByMigrash(migrash: string): Promise<ParcelIdentifyResult> {
  return parseJson(
    await fetch(`/api/map/migrash/${encodeURIComponent(migrash.trim())}`),
  );
}

/** @deprecated Use lookupParcelByMigrash */
export async function lookupParcelByGushHelka(
  gush: string,
  helka: string,
): Promise<ParcelIdentifyResult> {
  return parseJson(
    await fetch(
      `/api/map/parcel/${encodeURIComponent(gush)}/${encodeURIComponent(helka)}`,
    ),
  );
}
